import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Toolbar, IconButton } from '@mui/material';
import { DRAWER_WIDTH } from 'constants/index';
import { styled, useTheme } from '@mui/material/styles';

import { useLocation, useParams } from 'react-router-dom';
import { UsersBar } from 'components/GlobalAppbar/UsersBar';

import { GeneralIds, RoutePaths } from 'types/types';
import { ProjectSelectorBar } from './ProjectSelectorBar';
import { getRoutePath } from 'utils';
import { Menu } from '@mui/icons-material';
import { PatientsBar } from './PatientsBar';
import { Box, useMediaQuery } from '@mui/system';
import { BackButton } from 'components/BackButton';
import AppbarBreadcrumbs from 'components/AppbarBreadcrumbs/AppbarBreadcrumbs';
import UserMenu from './UserMenu';
import FormEditorControls from 'routes/FormTemplateEditor/FormEditor/FormEditorControls/FormEditorControls';
import { useCallback, useEffect, useState } from 'react';
import { FormBar } from './FormBar';

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  border: 'none',
  transition: theme.transitions.create(
    ['width', 'margin', 'background', 'box-shadow'],
    {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    },
  ),
  ...(open && {
    marginLeft: DRAWER_WIDTH,
    width: `calc(100% - ${DRAWER_WIDTH}px)`,
    transition: theme.transitions.create(
      ['width', 'margin', 'background', 'box-shadow'],
      {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      },
    ),
  }),
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  handleDrawerOpen?: () => void;
}

export function AppBar({ open, handleDrawerOpen }: AppBarProps) {
  const location = useLocation();
  const path = location.pathname;
  const ids = useParams<GeneralIds>();
  const [background, setBackground] = useState('none');
  const [boxShadow, setBoxShadow] = useState('none');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleScroll = useCallback(() => {
    if (window.scrollY > 0) {
      setBackground(theme.palette.background.default);
      setBoxShadow('0px 1px 4px rgba(0, 0, 0, 0.3)');
      return;
    }
    setBoxShadow('none');
    setBackground('none');
  }, [theme.palette.background.default]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const isFormFillPage =
    window.location.href.includes('/projects') &&
    window.location.href.includes('/patients') &&
    window.location.href.includes('/visits') &&
    window.location.href.includes('/formTemplateType') &&
    window.location.href.includes('/forms');

  return (
    <StyledAppBar position="fixed" open={open} sx={{ background, boxShadow }}>
      <Toolbar
        sx={{
          pr: 5,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" flexDirection={'row'}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: isMobile ? 1 : 5,
              ...(open && { display: 'none' }),
            }}
          >
            <Menu fontSize="small" />
          </IconButton>

          <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
            <BackButton />
            {isFormFillPage ? <FormBar /> : <AppbarBreadcrumbs />}
          </Box>
        </Box>
        <Box display="flex" alignItems={'center'} justifyContent={'start'}>
          {path === getRoutePath(RoutePaths.users, ids) && <UsersBar />}

          {path === RoutePaths.projectSelector && <ProjectSelectorBar />}

          {path === getRoutePath(RoutePaths.patients, ids) && <PatientsBar />}

          {(path === getRoutePath(RoutePaths.formTemplateEditor, ids) ||
            path === getRoutePath(RoutePaths.reportTemplateEditor, ids) ||
            path === getRoutePath(RoutePaths.sampleTemplateEditor, ids)) && (
            <FormEditorControls disabled={false} />
          )}

          {(path === getRoutePath(RoutePaths.formTemplateEditorView, ids) ||
            path ===
              getRoutePath(RoutePaths.sampleTemplateEditorView, ids)) && (
            <FormEditorControls disabled />
          )}

          <UserMenu />
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}
